import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import { ShortlistSortOrder, ShortlistSortProfileValue } from '@modules/shortlist/types'

type SortProfileValuesMap = Record<
  string,
  {
    label: string
    order: ShortlistSortOrder
    /**
     * Note: this is an object being used for API request
     */
    sort: Record<string, 'asc' | 'desc'>
    addCompanyIdsToRequest?: boolean
  }
>

export const DEFAULT_SHORTLIST_API_SORT: Record<string, 'desc'> = { createdAt: 'desc' }

export const sortFieldsWithCompanyIds: Record<string, boolean> = {
  statusType: true,
}

export const fieldNameToSortFieldMap: Record<string, string> = {
  company: 'companyName',
  contactStatus: 'statusType',
  futureEvents: 'futureEventEndDate',
  lastEvent: 'pastEventEndDate',
  note: 'noteUpdatedAt',
}

export const useSortProfileValuesMap = (): SortProfileValuesMap => {
  const { t } = useTranslation()

  const sortProfileValuesMap: SortProfileValuesMap = useMemo<SortProfileValuesMap>(
    () => ({
      [ShortlistSortProfileValue.ASSET_COMPANY_TITLE]: {
        label: t(translationKeys.ASSET_COMPANY_TITLE),
        order: ShortlistSortOrder.NOT_ASC_DESC,
        sort: { text: 'asc' },
      },
      [ShortlistSortProfileValue.COMPANY_NAME_ASC]: {
        label: t(translationKeys.COMPANY_NAME_ASC),
        order: ShortlistSortOrder.ASC,
        sort: { companyName: 'asc' },
      },
      [ShortlistSortProfileValue.COMPANY_NAME_DESC]: {
        label: t(translationKeys.COMPANY_NAME_DESC),
        order: ShortlistSortOrder.DESC,
        sort: { companyName: 'desc' },
      },
      [ShortlistSortProfileValue.DATE_ADDED_ASCENDING]: {
        label: t(translationKeys.DATE_ADDED_ASCENDING),
        order: ShortlistSortOrder.ASC,
        sort: { createdAt: 'asc' },
      },
      [ShortlistSortProfileValue.DATE_ADDED_DESCENDING]: {
        label: t(translationKeys.DATE_ADDED_DESCENDING),
        order: ShortlistSortOrder.DESC,
        sort: { createdAt: 'desc' },
      },
      [ShortlistSortProfileValue.NEWEST_EVENT]: {
        label: t(translationKeys.NEWEST_EVENT),
        order: ShortlistSortOrder.DESC,
        sort: { eventStartDate: 'desc' },
      },
      [ShortlistSortProfileValue.OLDEST_EVENT]: {
        label: t(translationKeys.OLDEST_EVENT),
        order: ShortlistSortOrder.ASC,
        sort: { eventStartDate: 'asc' },
      },
      [ShortlistSortProfileValue.MOST_RECENT_EVENT_FIRST]: {
        label: t(translationKeys.MOST_RECENT_EVENT_FIRST),
        order: ShortlistSortOrder.DESC,
        sort: { pastEventEndDate: 'desc' },
      },
      [ShortlistSortProfileValue.NEXT_EVENT]: {
        label: t(translationKeys.NEXT_EVENT),
        order: ShortlistSortOrder.ASC,
        sort: { futureEventEndDate: 'asc' },
      },
      [ShortlistSortProfileValue.STATUS_TYPE]: {
        label: t(translationKeys.STATUS_TYPE),
        order: ShortlistSortOrder.NOT_ASC_DESC,
        sort: { statusType: 'asc' },
        addCompanyIdsToRequest: true,
      },
      [ShortlistSortProfileValue.DATE_UPDATED_ASCENDING]: {
        label: t(translationKeys.DATE_UPDATED_ASCENDING),
        order: ShortlistSortOrder.ASC,
        sort: { updatedAt: 'asc' },
      },
      [ShortlistSortProfileValue.DATE_UPDATED_DESCENDING]: {
        label: t(translationKeys.DATE_UPDATED_DESCENDING),
        order: ShortlistSortOrder.DESC,
        sort: { updatedAt: 'desc' },
      },
      [ShortlistSortProfileValue.DONE]: {
        label: t(translationKeys.DONE),
        order: ShortlistSortOrder.NOT_ASC_DESC,
        sort: { nda: 'desc' },
      },
      [ShortlistSortProfileValue.NOT_DONE]: {
        label: t(translationKeys.NOT_DONE),
        order: ShortlistSortOrder.NOT_ASC_DESC,
        sort: { nda: 'asc' },
      },
      [ShortlistSortProfileValue.NEWEST_NOTE]: {
        label: t(translationKeys.NEWEST_NOTE),
        order: ShortlistSortOrder.DESC,
        sort: { noteUpdatedAt: 'desc' },
      },
      [ShortlistSortProfileValue.OLDEST_NOTE]: {
        label: t(translationKeys.OLDEST_NOTE),
        order: ShortlistSortOrder.ASC,
        sort: { noteUpdatedAt: 'asc' },
      },
      [ShortlistSortProfileValue.PRIORITY_HIGH_TO_NOT_SET]: {
        label: t(translationKeys.PRIORITY_HIGH_TO_NOT_SET),
        order: ShortlistSortOrder.ASC,
        sort: { priority: 'asc' },
      },
      [ShortlistSortProfileValue.PRIORITY_NOT_SET_TO_HIGH]: {
        label: t(translationKeys.PRIORITY_NOT_SET_TO_HIGH),
        order: ShortlistSortOrder.DESC,
        sort: { priority: 'desc' },
      },
      [ShortlistSortProfileValue.STATUS]: {
        label: t(translationKeys.STATUS),
        order: ShortlistSortOrder.ASC,
        sort: { tagId: 'asc' },
      },
      [ShortlistSortProfileValue.NEWEST_TEXTS]: {
        label: t(translationKeys.NEWEST_TEXTS),
        order: ShortlistSortOrder.ASC,
        sort: { customFieldValues: 'desc' },
      },
      [ShortlistSortProfileValue.OLDEST_TEXTS]: {
        label: t(translationKeys.OLDEST_TEXTS),
        order: ShortlistSortOrder.DESC,
        sort: { customFieldValues: 'asc' },
      },
      [ShortlistSortProfileValue.PROFILE_TITLE_TYPE]: {
        label: t(translationKeys.PROFILE_TITLE_TYPE),
        order: ShortlistSortOrder.ASC,
        sort: { profileType: 'asc' },
      },
      [ShortlistSortProfileValue.PROFILE_TITLE_ASC]: {
        label: t(translationKeys.PROFILE_TITLE_ASC),
        order: ShortlistSortOrder.ASC,
        sort: { text: 'asc' },
      },
      [ShortlistSortProfileValue.PROFILE_TITLE_DESC]: {
        label: t(translationKeys.PROFILE_TITLE_DESC),
        order: ShortlistSortOrder.DESC,
        sort: { text: 'desc' },
      },
      [ShortlistSortProfileValue.ALERT_DATE_ADDED_ASCENDING]: {
        label: t(translationKeys.ALERT_DATE_ADDED_ASCENDING),
        order: ShortlistSortOrder.ASC,
        sort: { createdAt: 'asc' },
      },
      [ShortlistSortProfileValue.ALERT_DATE_ADDED_DESCENDING]: {
        label: t(translationKeys.ALERT_DATE_ADDED_DESCENDING),
        order: ShortlistSortOrder.DESC,
        sort: { createdAt: 'desc' },
      },
      [ShortlistSortProfileValue.ALERT_DATE_UPDATE_ASCENDING]: {
        label: t(translationKeys.ALERT_DATE_UPDATE_ASCENDING),
        order: ShortlistSortOrder.ASC,
        sort: { alertUpdatedAt: 'asc' },
      },
      [ShortlistSortProfileValue.ALERT_DATE_UPDATE_DESCENDING]: {
        label: t(translationKeys.ALERT_DATE_UPDATE_DESCENDING),
        order: ShortlistSortOrder.DESC,
        sort: { alertUpdatedAt: 'desc' },
      },
      [ShortlistSortProfileValue.ALERT_FREQUENCY]: {
        label: t(translationKeys.ALERT_FREQUENCY),
        order: ShortlistSortOrder.ASC,
        sort: { alertFrequency: 'asc' },
      },
    }),
    [t],
  )

  return sortProfileValuesMap
}
